import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { useCallback, useRef } from 'react';
import { ContentRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfileLiteRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { SectionContentDto, SectionRankDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import {
  ContentListType,
  TrackerExtraInfoProps,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import { ImageRatio } from '@diritto/common/base/constants';
import { isPC } from 'constants/layout';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { SectionContentLiteItem } from 'app-components/home/sections/items/SectionContentLiteItem';
import { SectionSlideButton } from 'app-components/home/sections/components/SectionSlideButton';
import { SectionTitle } from 'app-components/home/sections/components/SectionTitle';
import { hideScrollbar } from 'styles/common';
import { useSlide } from 'ui-hooks/useSlide';
import { getValidContentAndProfile, useRearrangedContentIds } from 'utils/sectionContentUtils';
import { useBlockProfiles } from 'state-hooks/accounts/useBlockProfiles';

type SectionProps = {
  section: SectionContentDto | SectionRankDto;
  contents: Record<number, ContentRelayDto>;
  profileLites: Record<number, ProfileLiteRelayDto>;
  showCategory?: boolean;
};

const Height = 262;
const CoverImageWidth = 110;
const CoverImageHeight = CoverImageWidth * ImageRatio;
const VerticalGap = 8;
const HorizontalGap = 8;

export const SectionContentLites = ({
  section,
  contents,
  profileLites,
  showCategory = true,
  trackerExtraInfo,
}: SectionProps & TrackerExtraInfoProps) => {
  const { contentIds } = section;
  const { tracker } = useTrackingState();
  const slideRef = useRef<HTMLDivElement>(null);
  const { sessionSeed } = useAuthState();
  const blockedProfileIds = useBlockProfiles();
  const { handlePrevSlide, handleNextSlide, handleSlide, showSlideButton } = useSlide(slideRef, HorizontalGap);

  const rearrangedContentIds = useRearrangedContentIds(
    contentIds,
    section.shufflePreviewItem,
    section.previewItemCount,
    sessionSeed,
  );

  const handleOnClickTracker = useCallback(
    (contentId: number, listItemIndex: number) => {
      tracker.track(TrackingEventType.SelectContentListItem, {
        listType: ContentListType.SectionContent,
        contentId,
        listItemIndex,
        params: { sectionId: section.id, sectionDesign: section.design, sectionTitle: section.title },
        trackerExtraInfo,
      });
    },
    [section.design, section.id, section.title, tracker, trackerExtraInfo],
  );

  if (rearrangedContentIds.length === 0) {
    return null;
  }

  return (
    <div>
      <SectionTitle section={section} moreLink={section.moreLink} trackerExtraInfo={trackerExtraInfo} />
      <StyledWrapper>
        <SectionSlideButton
          showSlideButton={showSlideButton}
          handleNextSlide={handleNextSlide}
          handlePrevSlide={handlePrevSlide}
          // StyledContentMetaContainer의 height 만큼 버튼의 top을 위로 올려줌
          extraButtonTop={(-1 * (Height - CoverImageHeight)) / 2}
        />
        <StyledScrollContainer ref={slideRef} onScroll={handleSlide} rowCount={section.rowCount}>
          {rearrangedContentIds.map((contentId, index) => {
            const validationResult = getValidContentAndProfile(contentId, contents, profileLites, blockedProfileIds);
            if (!validationResult) {
              return null;
            }

            const { content, profile } = validationResult;

            return (
              <SectionContentLiteItem
                key={contentId}
                content={content}
                profile={profile}
                showCategory={showCategory}
                onClick={() => handleOnClickTracker(contentId, index)}
              />
            );
          })}
        </StyledScrollContainer>
      </StyledWrapper>
    </div>
  );
};

export default SectionContentLites;

const StyledWrapper = styled.div`
  position: relative;
`;

const StyledScrollContainer = styled.div(
  ({ rowCount = 1 }: { rowCount?: number }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    flex-flow: column wrap;
    align-content: flex-start;
    padding: 0 16px;
    overflow-y: hidden;
    overflow-x: scroll;
    ${hideScrollbar};
    gap: ${VerticalGap}px ${HorizontalGap}px;
    height: ${Height * rowCount + VerticalGap * (rowCount - 1)}px;
    @media (${isPC}) {
      padding: 0;
      margin: 0 15px;
      overflow-x: hidden;
    }
  `,
);
